import { defineStore } from 'pinia'
import { ref } from 'vue'

export type ThemePreference = 'light' | 'dark' | 'system'
export type PalettePreference = 'kipu' | 'cybernetic'

const STORAGE_KEY = 'relay-web-theme'
const PALETTE_STORAGE_KEY = 'relay-web-palette'
const DEFAULT_PALETTE: PalettePreference = 'kipu'

function readThemePreference(): ThemePreference {
  const stored = localStorage.getItem(STORAGE_KEY)
  return stored === 'light' || stored === 'dark' || stored === 'system'
    ? stored
    : 'system'
}

function readPalettePreference(): PalettePreference {
  const stored = localStorage.getItem(PALETTE_STORAGE_KEY)
  return stored === 'kipu' || stored === 'cybernetic'
    ? stored
    : DEFAULT_PALETTE
}

export const useThemeStore = defineStore('theme', () => {
  const preference = ref<ThemePreference>(readThemePreference())
  const palettePreference = ref<PalettePreference>(readPalettePreference())
  const isDark = ref(false)

  function getSystemPreference(): boolean {
    return window.matchMedia('(prefers-color-scheme: dark)').matches
  }

  function applyTheme() {
    const shouldBeDark =
      preference.value === 'dark' ||
      (preference.value === 'system' && getSystemPreference())

    document.documentElement.classList.add('theme-transitioning')

    if (shouldBeDark) {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }

    document.documentElement.dataset.palette = palettePreference.value
    isDark.value = shouldBeDark

    setTimeout(() => {
      document.documentElement.classList.remove('theme-transitioning')
    }, 300)
  }

  function setTheme(theme: ThemePreference) {
    preference.value = theme
    localStorage.setItem(STORAGE_KEY, theme)
    applyTheme()
  }

  function setPalette(palette: PalettePreference) {
    palettePreference.value = palette
    localStorage.setItem(PALETTE_STORAGE_KEY, palette)
    applyTheme()
  }

  function toggle() {
    setTheme(isDark.value ? 'light' : 'dark')
  }

  // Listen for system preference changes
  window
    .matchMedia('(prefers-color-scheme: dark)')
    .addEventListener('change', () => {
      if (preference.value === 'system') {
        applyTheme()
      }
    })

  // Apply on initialization
  applyTheme()

  return {
    preference,
    palettePreference,
    isDark,
    setTheme,
    setPalette,
    toggle,
  }
})
